import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ViewAIReportMode } from 'src/app/common/enums/enums';
import { DialogBaseComponent } from '@components/common/dialog/dialog-base/dialog-base.component';
import { TemplatesComponent } from './ai-reports-sub-menu/templates/templates.component';
import { ChatComponent } from './ai-reports-sub-menu/chat/chat.component';
import { HistoryComponent } from './ai-reports-sub-menu/history/history.component';
import { ChatClientService } from 'src/app/services/AIReports/chat-client.service';
import { AiChatFacade } from './ai-reports-sub-menu/chat/ai-chat.facade';
import { AiCookieService } from 'src/app/services/AIReports/ai-cookie.service';
import { AiCreditsService } from 'src/app/services/AIReports/ai-credits.service';
import { TopupReturnService } from 'src/app/services/AIReports/topup-return.service';
import { AiConsentService } from 'src/app/services/AIReports/ai-consent.service';
import { CreditsBarComponent } from './ai-reports-sub-menu/chat/components/credits-bar/credits-bar.component';
import { ConsentGateComponent } from './ai-reports-sub-menu/consent-gate/consent-gate.component';


@Component({
  selector: 'app-ai-reports',
  templateUrl: './ai-reports.component.html',
  styleUrl: './ai-reports.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TemplatesComponent,
    ChatComponent,
    HistoryComponent,
    CreditsBarComponent,
    ConsentGateComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ChatClientService, AiChatFacade]
})
export class AiReportsComponent extends DialogBaseComponent implements OnInit {

  @Input() data: any = { options: {} };
  private readonly aiCookieService = inject(AiCookieService);
  private readonly aiCreditsService = inject(AiCreditsService);
  private readonly topupReturnService = inject(TopupReturnService);
  private readonly aiConsentService = inject(AiConsentService);
  readonly ViewAIReportMode = ViewAIReportMode;

  readonly viewMode = signal<ViewAIReportMode>(ViewAIReportMode.Templates);
  readonly selectedPrompt = signal<string | null | undefined>(undefined);
  readonly consentAccepted = computed(() =>
    !this.aiConsentService.fetchFailed() && this.aiConsentService.status()?.accepted === true);

  constructor() {
    super(null, null);
  }

  ngOnInit(): void {
    this.aiCookieService.syncProjectToken();
    this.aiCreditsService.fetchBalance();
    this.aiConsentService.fetchStatus();

    // A pending Vipps topup reference means the user just returned from paying —
    // jump straight to Chat so the credits-bar's own ngOnInit (it's always mounted, just
    // hidden outside the Chat tab) picks it up and shows the processing/success screen,
    // instead of defaulting to Templates.
    if (this.topupReturnService.hasPending()) {
      this.changeViewMode(ViewAIReportMode.Chat);
    } else {
      this.viewMode.set(ViewAIReportMode.Templates);
    }
  }

  changeViewMode(mode: ViewAIReportMode): void {
    this.data.reference = { Id: 0, rawData: "" };

    if (mode === ViewAIReportMode.Templates) {
      this.data.options.primaryTabName = "lbl_templates";
      this.selectedPrompt.set(undefined);
    } else if (mode === ViewAIReportMode.History) {
      this.data.options.primaryTabName = "lbl_history";
      this.selectedPrompt.set(undefined);
    } else if (mode === ViewAIReportMode.Chat) {
      this.data.options.primaryTabName = "lbl_chat";
      this.aiCreditsService.fetchBalance();
    }

    this.viewMode.set(mode);
  }

  switchToChat(templatePrompt?: string | null): void {
    this.selectedPrompt.set(templatePrompt);
    this.changeViewMode(ViewAIReportMode.Chat);
  }

}
